import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { PawPrint, Lock, Check, AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function readResponseBody(res) {
  try {
    const txt = await res.clone().text();
    if (!txt) return {};
    try { return JSON.parse(txt); } catch { return { detail: txt }; }
  } catch {
    return {};
  }
}

function fallbackFromStatus(status) {
  if (status === 400) return "Invalid or expired reset link.";
  if (status === 422) return "Password must be at least 8 characters.";
  return "Something went wrong. Please try again.";
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("This reset link is missing its token."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await readResponseBody(res);
      if (!res.ok) {
        const raw = formatApiErrorDetail(data.detail);
        const msg = raw && raw !== "Something went wrong. Please try again."
          ? raw
          : fallbackFromStatus(res.status);
        setError(msg);
        return;
      }
      setDone(true);
      setTimeout(() => navigate("/"), 2500);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-['Figtree'] flex items-center justify-center px-4" style={{ background: "#FAF9F6" }}>
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-[#FF8A65] flex items-center justify-center">
            <PawPrint size={22} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="font-['Fredoka_One'] text-2xl text-slate-800 tracking-wide">Pet Atlas</span>
        </Link>

        <div className="bg-white rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.06)] border border-slate-100 p-8">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-[#388E3C]" />
              </div>
              <h1 className="text-xl font-extrabold text-slate-800 font-['Nunito'] mb-2">Password updated</h1>
              <p className="text-sm text-slate-500 mb-6">You can now sign in with your new password.</p>
              <Link
                to="/"
                data-testid="reset-success-signin"
                className="inline-block px-6 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors"
              >
                Go to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-slate-800 font-['Nunito'] mb-1">Choose a new password</h1>
              <p className="text-sm text-slate-500 mb-6">Pick a strong password of at least 8 characters.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div data-testid="reset-error" className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">New password</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="reset-password-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      minLength={8}
                      required
                      className="w-full border border-slate-200 rounded-xl pl-10 pr-[5.5rem] py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
                    />
                    <button
                      type="button"
                      data-testid="reset-password-toggle"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#FF8A65] bg-[#FFF5E6] hover:bg-[#FFDDB3] transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} strokeWidth={2.5} /> : <Eye size={16} strokeWidth={2.5} />}
                      <span>{showPassword ? "Hide" : "Show"}</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm new password</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="reset-confirm-input"
                      type={showConfirm ? "text" : "password"}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Retype password"
                      minLength={8}
                      required
                      className="w-full border border-slate-200 rounded-xl pl-10 pr-[5.5rem] py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
                    />
                    <button
                      type="button"
                      data-testid="reset-confirm-toggle"
                      onClick={() => setShowConfirm(v => !v)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#FF8A65] bg-[#FFF5E6] hover:bg-[#FFDDB3] transition-colors"
                    >
                      {showConfirm ? <EyeOff size={16} strokeWidth={2.5} /> : <Eye size={16} strokeWidth={2.5} />}
                      <span>{showConfirm ? "Hide" : "Show"}</span>
                    </button>
                  </div>
                </div>

                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 size={15} className="animate-spin" />}
                  Update password
                </button>

                <Link to="/" className="block text-center text-xs font-semibold text-slate-500 hover:text-[#FF8A65]">
                  ← Back to sign in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
